import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput, isValidIndianMobile } from '@/components/ui/phone-input';
import { clientSignup, requestOtp } from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { getTokenStore } from '@shared/api/token-store';
import { useAuthStore } from '@shared/stores/auth-store';
import { ME_QUERY_KEY, roleHome } from '@shared/hooks/use-me';
import { validateEmail } from '@shared/utils/validation';
import { EyeIcon, EyeOffIcon } from '@/components/layout/icons';
import { AuthShell } from './auth-shell';

export function SignupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mobile, setMobile] = useState('');
  const [validationError, setValidationError] = useState('');

  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  const sendOtpMutation = useMutation({
    mutationFn: () => requestOtp(email.trim(), 'CLIENT', true),
    onMutate: () => {
      setValidationError('');
      setSendingOtp(true);
    },
    onSuccess: () => {
      setOtpSent(true);
      setSendingOtp(false);
    },
    onError: (err) => {
      setValidationError(apiErrorMessage(err, 'Failed to send OTP. Please check your email.'));
      setSendingOtp(false);
    },
  });

  function handleSendOtp() {
    setValidationError('');
    setEmailTouched(true);
    if (!firstName.trim() || !lastName.trim()) {
      setValidationError(
        'Please fill in First name and Last name to request an OTP.',
      );
      return;
    }
    const result = validateEmail(email);
    if (!result.isValid) {
      setEmailError(result.error);
      setValidationError(result.error ?? 'Please enter a valid email address.');
      return;
    }
    setEmailError(null);
    sendOtpMutation.mutate();
  }

  function handleEmailChange(val: string) {
    setEmail(val);
    if (emailTouched) {
      const result = validateEmail(val);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  function handleEmailBlur() {
    setEmailTouched(true);
    if (email.trim()) {
      const result = validateEmail(email);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  const complete = useMutation({
    mutationFn: () =>
      clientSignup({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        mobile: mobile.trim() || undefined,
        otpCode: otpCode.trim(),
      }),
    onSuccess: (session) => {
      getTokenStore().setAccessToken(session.accessToken, 'client');
      setSession(session.user);
      qc.setQueryData(ME_QUERY_KEY, {
        ...session.user,
        avatarUrl: null,
        emailVerifiedAt: null,
        lastLoginAt: null,
        createdAt: new Date().toISOString(),
      });
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      navigate(roleHome(session.user.role), { replace: true });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setValidationError('');

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !otpCode.trim() || !password) {
      setValidationError('Please fill in all required fields.');
      return;
    }

    const emailResult = validateEmail(email);
    if (!emailResult.isValid) {
      setEmailError(emailResult.error);
      setValidationError(emailResult.error ?? 'Please enter a valid email address.');
      return;
    }

    if (otpCode.trim().length !== 6) {
      setValidationError('OTP must be a 6-digit number.');
      return;
    }

    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters long.');
      return;
    }

    if (mobile && !isValidIndianMobile(mobile)) {
      setValidationError('Please enter a valid 10-digit Indian mobile number.');
      return;
    }

    complete.mutate();
  }

  return (
    <AuthShell>
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900">Create Client Account</h1>
          <p className="mt-1 text-sm text-gray-600">
            Sign up for legal &amp; business documentation services
          </p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">First name</label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                disabled={otpSent}
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Last name</label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                disabled={otpSent}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Email address</label>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              required
              disabled={otpSent}
              className={emailError && !otpSent ? 'border-red-500 focus:border-red-600 focus:ring-red-500/30' : undefined}
            />
            {emailError && !otpSent && (
              <p className="mt-1 text-xs font-medium text-red-600">{emailError}</p>
            )}
          </div>

          {!otpSent ? (
            <Button type="button" block onClick={handleSendOtp} disabled={sendingOtp}>
              {sendingOtp ? 'Sending OTP…' : 'Send Verification OTP'}
            </Button>
          ) : (
            <>
              <div className="rounded-md bg-brand-50 p-2.5 border border-brand-200">
                <p className="text-xs text-brand-800 font-medium">
                  Verification code sent to <strong className="text-brand-900">{email}</strong>.
                  <button
                    type="button"
                    onClick={() => {
                      setOtpSent(false);
                      setEmailTouched(false);
                      setEmailError(null);
                    }}
                    className="ml-2 underline text-brand-700 font-semibold hover:text-brand-900"
                  >
                    Change Email
                  </button>
                </p>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  6-Digit Verification OTP
                </label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="Enter 6-digit OTP"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  Mobile <span className="text-gray-400">(optional)</span>
                </label>
                <PhoneInput value={mobile} onChange={setMobile} />
                <p className="mt-1 text-xs text-gray-500">Indian numbers only.</p>
              </div>

              {validationError ? <p className="text-sm text-red-600">{validationError}</p> : null}

              {complete.isError ? (
                <p className="text-sm text-red-600">{apiErrorMessage(complete.error)}</p>
              ) : null}

              <Button type="submit" block disabled={complete.isPending}>
                {complete.isPending ? 'Creating account…' : 'Sign Up'}
              </Button>
            </>
          )}

          {!otpSent && validationError ? (
            <p className="text-sm text-red-600">{validationError}</p>
          ) : null}

          <p className="pt-2 text-center text-xs text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-700 underline underline-offset-2">
              Log in
            </Link>
          </p>
        </form>
      </div>
    </AuthShell>
  );
}
